import * as endpoints from "../../lib/api/endpoints";
import * as error from "../../lib/api/error";
import * as queryStringHelpers from "../../lib/queryStringHelpers";
import * as routes from "../../constants/routes";
import ActionButton, {ActionButtonStyle} from "../ActionButton";
import CardBanner from "../CardBanner";
import ErrorBoundary from "../ErrorBoundary";
import Footer from "../Footer";
import logger from "../../lib/logger";
import PersonalReferralCard from "../PersonalReferralCard";
import React, {useEffect, useRef, useState} from "react";
import referralShareImage from "../../images/referral-share.png";
import referralShareImage2 from "../../images/referral-share@2x.png";
import referralShareImage3 from "../../images/referral-share@3x.png";
import SetNoIndex from "../../lib/SetNoIndex";
import Spinner from "../Spinner";
import styles from "./ReferralSharePage.module.css";
import TextLink from "../TextLink";
import usePageView from "../../lib/usePageView";
import {Column, Container, Row} from "../grid";
import {RouteComponentProps} from "react-router-dom";
import {StaticContext} from "react-router";

const strings = {
    bannerDescription: "You and a friend can both earn a $50 statement credit!",
    bannerTitle: "Good things are meant to be shared.",
    bannerExtraInfo: "See Referral Program ",
    finished: "I’m Finished Sharing, Continue",
    forMore: " for more details",
    referralShareImage: "excited person",
    shareDescription:
        "We’ve sent you a text and an email with your Personal Referral Link below. Get started by sharing your Personal Referral Link now!",
    terms: "Terms and Conditions",
    title: "1FB USA Referral Share",
};

const TERMS_LINK = "/referral_program_terms_and_conditions.pdf";

type RouterState = {
    isSecured?: boolean;
};

export default function ReferralSharePage(
    props: RouteComponentProps<undefined, StaticContext, RouterState>,
) {
    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const [shortCode, setShortCode] = useState("");
    const [loading, setLoading] = useState(false);

    const queryParams = queryStringHelpers.objectify(window.location.search);

    useEffect(() => {
        document.title = strings.title;
    }, []);

    SetNoIndex();
    usePageView(strings.title);

    function getReferralShareImage() {
        return (
            <img
                className={styles.referralShareImage}
                src={referralShareImage}
                srcSet={`${referralShareImage} 1x, ${referralShareImage2} 2x, ${referralShareImage3} 3x`}
                alt={strings.referralShareImage}
            />
        );
    }

    function redirectUser(route: string) {
        if (!isMountedRef.current) {
            return;
        }
        const destination = `${process.env.REACT_APP_BASE_PATH}${route}${window.location.search}`;
        window.location.replace(destination);
    }

    function callLeadBeforeRedirect(route: string) {
        setLoading(true);
        endpoints
            .applicationLead(
                [
                    {
                        field: "referral-share-continue",
                        value: "",
                        timestamp: new Date().toISOString(),
                    },
                ],
                queryParams.dsn,
                window.location.href,
            )
            .catch((err) => {
                error.reportAxios(err, "callApplicationLead");
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                redirectUser(route);
            });
    }

    useEffect(() => {
        setLoading(true);
        endpoints
            .viewedReferAFriend()
            .then((data) => {
                if (!isMountedRef.current) {
                    return;
                }
                setShortCode(data.shortCode);
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                logger.log(err);
                redirectUser(routes.REFERRAL_ERROR);
            })
            .finally(() => setLoading(false));
    }, []);

    return (
        <ErrorBoundary>
            <CardBanner
                cardImage={getReferralShareImage()}
                renderInterestRates={false}
                renderOnlyCard={true}
                logoClassName={styles.logo}
            >
                <div className={styles.bannerTitle}>{strings.bannerTitle}</div>
                <div className={styles.bannerDescription}>{strings.bannerDescription}</div>
                <div className={styles.bannerExtraInfo}>
                    {strings.bannerExtraInfo}
                    <TextLink className={styles.textLink} text={strings.terms} to={TERMS_LINK} />
                    {strings.forMore}
                </div>
                <div className={styles.bannerShareDescription}>{strings.shareDescription}</div>
            </CardBanner>

            <div className={styles.roundedContainer}>
                {shortCode && shortCode.length ? (
                    <PersonalReferralCard
                        link={`https://apply.1fbusa.com/r/${shortCode}`}
                        roundedCardClassName={styles.roundedCard}
                        invitationCode={queryParams.dsn ?? undefined}
                    />
                ) : (
                    <Spinner isVisible={true} className={styles.loaderAppearance} />
                )}
            </div>
            <Container className={styles.container}>
                <Row>
                    <Column largeOffset={1} large={10} mediumOffset={1} medium={6}>
                        <Column largeOffset={3} large={4} mediumOffset={2} medium={4}>
                            <ActionButton
                                className={styles.finishedButton}
                                onClick={() => {
                                    if (props.location.state?.isSecured) {
                                        callLeadBeforeRedirect(routes.APPROVED_SECURED);
                                    } else {
                                        callLeadBeforeRedirect(routes.APPROVED_DEFAULT);
                                    }
                                }}
                                disabled={loading}
                                label={strings.finished}
                                style={ActionButtonStyle.outlined}
                                fullWidth={true}
                            />
                        </Column>
                    </Column>
                </Row>
            </Container>
            <Footer />
        </ErrorBoundary>
    );
}
